import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { PacingSparkline } from '../../types/pacing-board.types';

interface PacingBoardSparklineProps {
  sparkline: PacingSparkline | null | undefined;
  height?: number;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDateLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function PacingBoardSparkline({ sparkline, height = 140 }: PacingBoardSparklineProps) {
  if (!sparkline?.dates?.length) {
    return (
      <div className="flex h-[140px] items-center justify-center text-xs text-[#9CA3AF]">
        No spend data available
      </div>
    );
  }

  const data = sparkline.dates.map((date, index) => ({
    date,
    label: formatDateLabel(date),
    ideal: sparkline.ideal[index] ?? 0,
    actual: sparkline.actual[index] ?? 0,
  }));

  return (
    <div className="rounded-xl border border-[#EDEEF2] bg-white p-4">
      <div className="mb-3">
        <div className="text-sm font-semibold tracking-[-0.2px] text-[#0D0F1A]">Cumulative spend</div>
        <div className="text-xs text-[#5E6278]">Actual vs ideal pacing curve</div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 10, fill: '#9CA3AF' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
            width={42}
          />
          <Tooltip
            formatter={(value: number, name: string) => [
              formatCurrency(value),
              name === 'ideal' ? 'Ideal' : 'Actual',
            ]}
            labelFormatter={(label) => String(label)}
            contentStyle={{
              fontSize: 12,
              borderRadius: 8,
              border: '1px solid #EDEEF2',
            }}
          />
          <Line
            type="monotone"
            dataKey="ideal"
            stroke="#2563EB"
            strokeWidth={2}
            dot={false}
            strokeDasharray="4 4"
          />
          <Line
            type="monotone"
            dataKey="actual"
            stroke="#EF4444"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-2 flex gap-4 text-[11px] text-[#5E6278]">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-4 border-t-2 border-dashed border-[#2563EB]" />
          Ideal
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-4 bg-[#EF4444]" />
          Actual
        </span>
      </div>
    </div>
  );
}
